import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import {
  Button,
  Callout,
  ChevronRightIcon,
  CountChip,
  DateInput,
  DateTimeInput,
  MapPinIcon,
  NumberInput,
  PlusIcon,
  SegmentedControl,
  Select,
  ShipIcon,
  TextInput,
  TrashIcon,
  UserIcon,
} from "../../components/ui";
import { useTheme } from "../../contexts/ThemeContext";
import { cn } from "../../lib/utils";
import { fetcher } from "../../services/fetcher";
import {
  createCrewTravel,
  createCrewTravelFromReplacement,
  updateCrewTravel,
} from "../../services/mutations";
import { normalizePaginatedListResponse } from "../../services/paginatedList";
import { CrewBackButton } from "./CrewBackButton";
import { CrewReportFilterField } from "./CrewReportSearchHeader";
import { crewDisplayName } from "./formMappers";
import type {
  CrewFile,
  CrewReplacementCandidateRow,
  CrewReplacementResponse,
  CrewTravelKind,
  CrewTravelPassenger,
  CrewTravelRow,
  CrewTravelSegment,
  CrewTravelSegmentKind,
  CrewTravelSide,
  CrewTravelStatus,
  CrewTravelTravelerRole,
  CrewVesselListResponse,
  CrewVesselListRow,
} from "./types";

type CrewPickOption = {
  value: string;
  label: string;
  description?: string;
  crewId: number;
};

const emptyPassenger = (
  side: CrewTravelSide,
  role: CrewTravelTravelerRole = "OTHER",
): CrewTravelPassenger => ({
  side,
  role,
  crewId: null,
  displayName: "",
  nationalityId: null,
  nationality: null,
  passport: null,
  dateOfBirth: null,
  notes: null,
  sortOrder: 0,
});

const emptySegment = (
  side: CrewTravelSide | null = null,
  kind: CrewTravelSegmentKind = "FLIGHT",
  segmentTypeId: number | null = null,
): CrewTravelSegment => ({
  passengerId: null,
  side,
  segmentTypeId,
  kind,
  kindName: null,
  travelDate: null,
  startAt: null,
  endAt: null,
  fromPlace: null,
  toPlace: null,
  reference: null,
  notes: null,
  cost: null,
  currency: "EUR",
  sortOrder: 0,
});

const ROLE_OPTIONS = [
  { value: "CREW", label: "Crew" },
  { value: "SUPERINTENDENT", label: "Superintendent" },
  { value: "AUDITOR", label: "Auditor" },
  { value: "TECHNICIAN", label: "Technician" },
  { value: "SPOUSE", label: "Spouse" },
  { value: "CHILD", label: "Child" },
  { value: "AGENT", label: "Agent" },
  { value: "OTHER", label: "Other" },
];

/** Shared control height across the Crew Changes Cost form. */
const FORM_CTRL_CLASS = "!h-10 !min-h-10 !py-0 box-border";
const FORM_SELECT_TRIGGER_CLASS = "!h-10 !min-h-10 !py-0 box-border";

const SIDE_OPTIONS_CHANGE = [
  { value: "SOFF", label: "Sign-off (outgoing)" },
  { value: "SON", label: "Sign-on (relief)" },
];

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "BOOKED", label: "Booked" },
  { value: "ISSUED", label: "Issued" },
  { value: "CANCELLED", label: "Cancelled" },
];

function parseKindParam(raw: string | null): CrewTravelKind | null {
  const v = raw?.trim().toUpperCase();
  if (v === "VISIT" || v === "CREW_CHANGE") return v;
  return null;
}

const FormPanel: React.FC<{
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Tighter padding for dense forms */
  bodyClassName?: string;
}> = ({
  title,
  subtitle,
  icon,
  action,
  children,
  className,
  bodyClassName,
}) => {
  const { colors } = useTheme();
  return (
    <section
      className={cn(
        "rounded-lg border shadow-sm overflow-hidden flex flex-col min-h-0",
        colors.surface,
        colors.border,
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-2 border-b px-3 py-1.5",
          colors.border,
          colors.headerBg,
        )}
      >
        <div className="min-w-0 flex items-center gap-2">
          {icon ? (
            <span className={cn("shrink-0", colors.textMuted)}>{icon}</span>
          ) : null}
          <div className="min-w-0 flex items-baseline gap-2">
            <h3 className={cn("text-sm font-semibold", colors.text)}>
              {title}
            </h3>
            {subtitle ? (
              <p
                className={cn(
                  "text-[11px] truncate hidden sm:block",
                  colors.textMuted,
                )}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {action}
      </div>
      <div className={cn("p-3 flex-1 min-h-0", bodyClassName)}>{children}</div>
    </section>
  );
};

const TravelTicketFormPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !idParam || idParam === "new";
  const travelId = !isNew ? Number(idParam) : NaN;
  const replacementIdParam = searchParams.get("replacementId");
  const kindParam = parseKindParam(searchParams.get("kind"));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<CrewTravelKind>(kindParam ?? "VISIT");
  const [status, setStatus] = useState<CrewTravelStatus>("DRAFT");
  const [vesselId, setVesselId] = useState("");
  const [purpose, setPurpose] = useState("");
  const [notes, setNotes] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [changePortId, setChangePortId] = useState("");
  const [replacementId, setReplacementId] = useState<number | null>(null);
  const [passengers, setPassengers] = useState<CrewTravelPassenger[]>(() =>
    (kindParam ?? "VISIT") === "CREW_CHANGE"
      ? [emptyPassenger("SOFF", "CREW")]
      : [emptyPassenger("VISIT", "SUPERINTENDENT")],
  );
  const [segments, setSegments] = useState<CrewTravelSegment[]>(() =>
    (kindParam ?? "VISIT") === "CREW_CHANGE"
      ? [emptySegment("SOFF")]
      : [emptySegment("VISIT")],
  );
  const [booted, setBooted] = useState(false);

  const { data: lookups } = useSWR<{
    vessels: Array<{ id: number; vesselName: string }>;
    ports: Array<{ id: number; name: string }>;
    nationalities: Array<{
      id: number;
      name: string;
      code?: string | null;
      abrv?: string | null;
    }>;
    currencies: Array<{
      id: number;
      code: string;
      name: string;
      symbol?: string | null;
    }>;
  }>("/crew/lookups", fetcher, { revalidateOnFocus: false });

  const { data: segmentTypesRaw } = useSWR(
    "/crew-lookups/travel-segment-types?page=1&limit=500",
    fetcher,
    { revalidateOnFocus: false },
  );
  const segmentTypeRows = useMemo(() => {
    const normalized = normalizePaginatedListResponse<{
      id: number;
      code?: string | null;
      name: string;
      isActive: boolean;
      sortOrder: number;
    }>(segmentTypesRaw);
    return [...(normalized.data ?? [])].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
    );
  }, [segmentTypesRaw]);

  const segmentKindOptions = useMemo(() => {
    const selectedCodes = new Set(
      segments.map((s) => (s.kind || "").toUpperCase()).filter(Boolean),
    );
    const selectedIds = new Set(
      segments
        .map((s) => s.segmentTypeId)
        .filter((id): id is number => id != null && id > 0),
    );
    return segmentTypeRows
      .filter(
        (t) =>
          t.isActive ||
          selectedIds.has(t.id) ||
          (t.code ? selectedCodes.has(t.code.toUpperCase()) : false),
      )
      .map((t) => ({
        value: (t.code || String(t.id)).toUpperCase(),
        label: t.name,
        id: t.id,
      }));
  }, [segmentTypeRows, segments]);

  const defaultSegmentKind = useMemo(() => {
    const flight = segmentKindOptions.find((o) => o.value === "FLIGHT");
    return flight ?? segmentKindOptions[0] ?? null;
  }, [segmentKindOptions]);

  const makeEmptySegment = useCallback(
    (side: CrewTravelSide | null = null): CrewTravelSegment =>
      emptySegment(
        side,
        defaultSegmentKind?.value ?? "FLIGHT",
        defaultSegmentKind?.id ?? null,
      ),
    [defaultSegmentKind],
  );

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const vesselListUrl =
    kind === "CREW_CHANGE" && vesselId
      ? `/crew/vessel-list?vesselId=${encodeURIComponent(vesselId)}&periodFrom=${todayIso}&periodTo=${todayIso}`
      : null;
  const replacementsUrl =
    kind === "CREW_CHANGE" && vesselId
      ? `/crew/replacements?vesselId=${encodeURIComponent(vesselId)}&includeCancelled=0`
      : null;

  const { data: vesselList } = useSWR<CrewVesselListResponse>(
    vesselListUrl,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );
  const { data: replData } = useSWR<CrewReplacementResponse>(
    replacementsUrl,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const onboardRows = useMemo(
    () =>
      (vesselList?.rows ?? []).filter(
        (r) => r.status === "ONB" || (!r.signOffDate && !r.status),
      ),
    [vesselList?.rows],
  );

  const selectedSoffCrewId = useMemo(() => {
    const soff = passengers.find((p) => p.side === "SOFF" && p.crewId != null);
    return soff?.crewId ?? null;
  }, [passengers]);

  const matchingReplacement = useMemo(() => {
    const rows = replData?.rows ?? [];
    if (replacementId != null) {
      const byId = rows.find((r) => r.id === replacementId);
      if (byId) return byId;
    }
    if (selectedSoffCrewId == null) return null;
    return (
      rows.find(
        (r) =>
          r.outgoingCrewId === selectedSoffCrewId &&
          r.status !== "CANCELLED" &&
          r.status !== "COMPLETED",
      ) ??
      rows.find((r) => r.outgoingCrewId === selectedSoffCrewId) ??
      null
    );
  }, [replData?.rows, replacementId, selectedSoffCrewId]);

  const soffCrewOptions = useMemo(() => {
    const opts: CrewPickOption[] = onboardRows.map((r: CrewVesselListRow) => {
      const name = [r.lastName, r.firstName].filter(Boolean).join(" ");
      return {
        value: String(r.crewId),
        label: name || `Crew #${r.crewId}`,
        description: [r.lastRank, r.nationality].filter(Boolean).join(" · "),
        crewId: r.crewId,
      };
    });
    const ensure = (crewId: number, label: string, description?: string) => {
      if (opts.some((o) => o.crewId === crewId)) return;
      opts.unshift({
        value: String(crewId),
        label: label || `Crew #${crewId}`,
        description,
        crewId,
      });
    };
    if (matchingReplacement) {
      ensure(
        matchingReplacement.outgoingCrewId,
        matchingReplacement.outgoingCrewName,
        matchingReplacement.rank ?? "Outgoing",
      );
    }
    for (const p of passengers) {
      if (p.side !== "SOFF" || p.crewId == null) continue;
      ensure(p.crewId, p.displayName || `Crew #${p.crewId}`, "Saved on ticket");
    }
    return opts;
  }, [onboardRows, passengers, matchingReplacement]);

  const sonCrewOptions = useMemo(() => {
    const opts: CrewPickOption[] = [];
    const seen = new Set<number>();
    const pushCandidate = (c: {
      crewId: number;
      crewName?: string | null;
      crewRef?: string | null;
      rank?: string | null;
      nationalityCode?: string | null;
      isSelected?: boolean;
    }) => {
      if (seen.has(c.crewId)) return;
      seen.add(c.crewId);
      opts.push({
        value: String(c.crewId),
        label: c.crewName || `Crew #${c.crewId}`,
        description: [
          c.isSelected ? "Selected relief" : null,
          c.crewRef ? `#${c.crewRef}` : null,
          c.rank,
          c.nationalityCode,
        ]
          .filter(Boolean)
          .join(" · "),
        crewId: c.crewId,
      });
    };

    if (matchingReplacement) {
      for (const c of matchingReplacement.candidates ?? []) {
        pushCandidate(c as CrewReplacementCandidateRow);
      }
      if (
        matchingReplacement.incomingCrewId != null &&
        !seen.has(matchingReplacement.incomingCrewId)
      ) {
        pushCandidate({
          crewId: matchingReplacement.incomingCrewId,
          crewName: matchingReplacement.incomingCrewName,
          isSelected: true,
        });
      }
    }

    for (const p of passengers) {
      if (p.side !== "SON" || p.crewId == null || seen.has(p.crewId)) continue;
      opts.unshift({
        value: String(p.crewId),
        label: p.displayName || `Crew #${p.crewId}`,
        description: "Saved on ticket",
        crewId: p.crewId,
      });
      seen.add(p.crewId);
    }
    return opts;
  }, [matchingReplacement, passengers]);

  const enrichCrewPick = useCallback(
    async (idx: number, crewId: number, fallbackLabel: string) => {
      try {
        const crew = (await fetcher(`/crew/${crewId}`)) as CrewFile;
        setPassengers((prev) =>
          prev.map((row, i) => {
            if (i !== idx || row.crewId !== crewId) return row;
            return {
              ...row,
              displayName: crewDisplayName(crew) || fallbackLabel,
              role: "CREW",
              nationalityId: crew.nationality?.id ?? null,
              nationality: crew.nationality?.name ?? null,
              dateOfBirth: crew.birthDate
                ? String(crew.birthDate).slice(0, 10)
                : null,
            };
          }),
        );
      } catch {
        // Keep the label from the list option if detail fetch fails.
      }
    },
    [],
  );

  const selectChangeCrew = useCallback(
    (
      idx: number,
      side: "SOFF" | "SON",
      crewId: number | null,
      options: CrewPickOption[],
    ) => {
      const pick = options.find((o) => o.crewId === crewId);
      const clearSon = side === "SOFF" && crewId !== selectedSoffCrewId;

      if (crewId == null) {
        setPassengers((prev) =>
          prev.map((row, i) => {
            if (i === idx || (clearSon && row.side === "SON")) {
              return {
                ...row,
                crewId: null,
                displayName: "",
                nationalityId: null,
                nationality: null,
                dateOfBirth: null,
              };
            }
            return row;
          }),
        );
        return;
      }

      setPassengers((prev) =>
        prev.map((row, i) => {
          if (clearSon && row.side === "SON") {
            return {
              ...row,
              crewId: null,
              displayName: "",
              nationalityId: null,
              nationality: null,
              dateOfBirth: null,
            };
          }
          if (i !== idx) return row;
          return {
            ...row,
            crewId,
            displayName: pick?.label ?? "",
            role: "CREW",
          };
        }),
      );
      void enrichCrewPick(idx, crewId, pick?.label ?? "");
    },
    [enrichCrewPick, selectedSoffCrewId],
  );

  const initialsFor = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
  };

  const detailUrl =
    Number.isFinite(travelId) && travelId > 0
      ? `/crew/travels/${travelId}`
      : null;
  const { data: existing, isLoading } = useSWR<CrewTravelRow>(
    detailUrl,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const vesselOptions = useMemo(
    () =>
      (lookups?.vessels ?? []).map((v) => ({
        value: String(v.id),
        label: v.vesselName,
      })),
    [lookups?.vessels],
  );
  const portOptions = useMemo(
    () => [
      { value: "", label: "—" },
      ...(lookups?.ports ?? []).map((p) => ({
        value: String(p.id),
        label: p.name,
      })),
    ],
    [lookups?.ports],
  );
  const nationalityOptionsBase = useMemo(
    () =>
      (lookups?.nationalities ?? []).map((n) => ({
        value: String(n.id),
        label: n.code || n.abrv ? `${n.name} (${n.code || n.abrv})` : n.name,
      })),
    [lookups?.nationalities],
  );
  const currencyOptions = useMemo(() => {
    const fromLookup = (lookups?.currencies ?? []).map((c) => ({
      value: c.code,
      label: c.symbol ? `${c.code} (${c.symbol})` : c.code,
    }));
    if (fromLookup.length) return fromLookup;
    return [
      { value: "EUR", label: "EUR" },
      { value: "USD", label: "USD" },
      { value: "GBP", label: "GBP" },
    ];
  }, [lookups?.currencies]);

  const namedTravelerCount = useMemo(
    () => passengers.filter((p) => p.displayName.trim()).length,
    [passengers],
  );
  const filledSegmentCount = useMemo(
    () =>
      segments.filter(
        (s) =>
          s.fromPlace?.trim() ||
          s.toPlace?.trim() ||
          s.reference?.trim() ||
          s.travelDate ||
          s.startAt ||
          s.endAt ||
          s.cost != null,
      ).length,
    [segments],
  );

  useEffect(() => {
    if (!isNew || booted) return;
    const rid = replacementIdParam ? Number(replacementIdParam) : NaN;
    if (!Number.isFinite(rid) || rid <= 0) {
      setBooted(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const row = (await createCrewTravelFromReplacement(
          rid,
        )) as CrewTravelRow;
        if (cancelled) return;
        navigate(`/crew/crew-changes-cost/${row.id}/edit`, { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || "Could not create ticket from replacement",
          );
          setBooted(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, replacementIdParam, booted, navigate]);

  useEffect(() => {
    if (!existing || booted) return;
    setKind(existing.kind);
    setStatus(existing.status);
    setVesselId(String(existing.vesselId));
    setPurpose(existing.purpose || "");
    setNotes(existing.notes || "");
    setDateFrom(existing.dateFrom || "");
    setDateTo(existing.dateTo || "");
    setChangePortId(
      existing.changePortId != null ? String(existing.changePortId) : "",
    );
    setReplacementId(existing.replacementId);
    setPassengers(
      existing.passengers?.length
        ? existing.passengers
        : [emptyPassenger(existing.kind === "VISIT" ? "VISIT" : "SOFF")],
    );
    setSegments(
      existing.segments?.length ? existing.segments : [emptySegment()],
    );
    setBooted(true);
  }, [existing, booted]);

  useEffect(() => {
    if (!isNew || replacementIdParam || booted) return;
    setBooted(true);
  }, [isNew, replacementIdParam, booted]);

  const onKindChange = (next: CrewTravelKind) => {
    setKind(next);
    if (next === "VISIT") {
      setPassengers((prev) => {
        const named = prev.filter((p) => p.displayName.trim());
        if (!named.length) return [emptyPassenger("VISIT", "SUPERINTENDENT")];
        return named.map((p) => ({ ...p, side: "VISIT" as const }));
      });
      setSegments((prev) =>
        prev.length
          ? prev.map((s) => ({ ...s, side: "VISIT" as const }))
          : [emptySegment("VISIT")],
      );
    } else {
      setPassengers((prev) => {
        const soff =
          prev.find((p) => p.side === "SOFF") ??
          prev.find((p) => p.displayName.trim()) ??
          emptyPassenger("SOFF", "CREW");
        const son = prev.find((p) => p.side === "SON");
        const nextPassengers: CrewTravelPassenger[] = [
          { ...soff, side: "SOFF", role: "CREW" },
        ];
        if (son && (son.displayName.trim() || son.crewId)) {
          nextPassengers.push({
            ...son,
            side: "SON",
            role: son.role === "CREW" ? "CREW" : son.role,
          });
        }
        return nextPassengers;
      });
      setSegments((prev) =>
        prev.length
          ? prev.map((s) =>
              s.side === "VISIT" || !s.side
                ? { ...s, side: "SOFF" as const }
                : s,
            )
          : [emptySegment("SOFF")],
      );
    }
  };

  const save = useCallback(async () => {
    const vid = Number(vesselId);
    if (!Number.isFinite(vid) || vid <= 0) {
      setError("Vessel is required");
      return;
    }
    const named = passengers.filter((p) => p.displayName.trim());
    if (!named.length) {
      setError("Add at least one traveler with a name");
      return;
    }
    if (kind === "CREW_CHANGE" && !named.some((p) => p.side === "SOFF")) {
      setError("Changing crew tickets need a sign-off (outgoing) traveler");
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      kind,
      status,
      vesselId: vid,
      replacementId,
      purpose: purpose.trim() || null,
      notes: notes.trim() || null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
      changePortId: changePortId ? Number(changePortId) : null,
      passengers: passengers
        .filter((p) => p.displayName.trim())
        .map((p, i) => ({
          side: p.side,
          role: p.role,
          crewId: p.crewId,
          nationalityId: p.nationalityId,
          displayName: p.displayName.trim(),
          passport: p.passport?.trim() || null,
          dateOfBirth: p.dateOfBirth || null,
          notes: p.notes?.trim() || null,
          sortOrder: i,
        })),
      segments: segments
        .filter(
          (s) =>
            s.fromPlace?.trim() ||
            s.toPlace?.trim() ||
            s.reference?.trim() ||
            s.travelDate ||
            s.startAt ||
            s.endAt ||
            s.cost != null,
        )
        .map((s, i) => ({
          side: s.side,
          segmentTypeId: s.segmentTypeId ?? undefined,
          kind: s.kind,
          travelDate: s.travelDate || null,
          startAt: s.startAt || null,
          endAt: s.endAt || null,
          fromPlace: s.fromPlace?.trim() || null,
          toPlace: s.toPlace?.trim() || null,
          reference: s.reference?.trim() || null,
          notes: s.notes?.trim() || null,
          cost: s.cost,
          currency: s.currency?.trim() || null,
          sortOrder: i,
        })),
    };
    try {
      if (isNew) {
        const row = (await createCrewTravel(payload)) as CrewTravelRow;
        navigate(`/crew/crew-changes-cost/${row.id}`, { replace: true });
      } else {
        await updateCrewTravel(travelId, payload);
        navigate(`/crew/crew-changes-cost/${travelId}`, { replace: true });
      }
    } catch (err) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not save Crew Changes Cost",
      );
    } finally {
      setSaving(false);
    }
  }, [
    vesselId,
    passengers,
    kind,
    status,
    replacementId,
    purpose,
    notes,
    dateFrom,
    dateTo,
    changePortId,
    segments,
    isNew,
    travelId,
    navigate,
  ]);

  const canRemovePassenger = (p: CrewTravelPassenger) => {
    if (kind === "CREW_CHANGE") {
      return !(
        p.side === "SOFF" &&
        passengers.filter((row) => row.side === "SOFF").length <= 1
      );
    }
    return passengers.length > 1;
  };

  if (isLoading && !isNew) {
    return (
      <div className={cn("p-4 text-sm", colors.textSecondary)}>Loading…</div>
    );
  }

  const pageTitle = isNew
    ? kind === "VISIT"
      ? "New visit"
      : "New crew change"
    : kind === "VISIT"
      ? "Edit visit"
      : "Edit crew change";

  return (
    <div className="h-full flex flex-col min-h-0 gap-2">
      <section
        className={cn(
          "shrink-0 rounded-lg border shadow-sm px-3 py-2",
          colors.surface,
          colors.border,
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <CrewBackButton
            fallbackPath={
              !isNew && Number.isFinite(travelId)
                ? `/crew/crew-changes-cost/${travelId}`
                : "/crew/crew-changes-cost"
            }
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h1
                className={cn(
                  "text-sm font-semibold tracking-tight",
                  colors.text,
                )}
              >
                {pageTitle}
              </h1>
              {replacementId ? (
                <CountChip title="Linked replacement">
                  #{replacementId}
                </CountChip>
              ) : null}
              {kind === "CREW_CHANGE" ? (
                <span
                  className={cn(
                    "inline-flex h-5 items-center rounded px-1.5 text-[10px] font-semibold",
                    isDark
                      ? "bg-amber-500/20 text-amber-200"
                      : "bg-amber-100 text-amber-900",
                  )}
                >
                  Crew change
                </span>
              ) : (
                <span
                  className={cn(
                    "inline-flex h-5 items-center rounded px-1.5 text-[10px] font-semibold",
                    isDark
                      ? "bg-sky-500/20 text-sky-200"
                      : "bg-sky-100 text-sky-900",
                  )}
                >
                  Visit
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!replacementId ? (
              <SegmentedControl
                aria-label="Ticket type"
                size="sm"
                value={kind}
                onChange={(v) => onKindChange(v)}
                options={[
                  { id: "VISIT", label: "Visit" },
                  { id: "CREW_CHANGE", label: "Changing crew" },
                ]}
              />
            ) : null}
            <div className="w-[8.5rem]">
              <Select
                value={status}
                onChange={(e) =>
                  setStatus(String(e.target.value) as CrewTravelStatus)
                }
                options={STATUS_OPTIONS}
                triggerClassName={FORM_SELECT_TRIGGER_CLASS}
              />
            </div>
          </div>
        </div>
      </section>

      {error ? (
        <Callout variant="danger" title="Could not save" role="alert">
          {error}
        </Callout>
      ) : null}

      <div className="flex-1 min-h-0 overflow-auto space-y-2 pb-2">
        <FormPanel
          title="Travel Details"
          subtitle={
            kind === "VISIT"
              ? "Vessel, window, port"
              : "Vessel, change window, port"
          }
          icon={<ShipIcon className="h-3.5 w-3.5" />}
        >
          <div className="space-y-2">
            <div className="flex flex-wrap items-end gap-2">
              <CrewReportFilterField
                label="Vessel"
                width="lg"
              >
                <Select
                  value={vesselId}
                  onChange={(e) => setVesselId(String(e.target.value))}
                  options={vesselOptions}
                  placeholder="Select vessel…"
                  searchPlaceholder="Search vessels…"
                  disabled={!!replacementId}
                  triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                />
              </CrewReportFilterField>
              <CrewReportFilterField
                label="From"
                width="sm"
                className="w-[10rem]"
              >
                <DateInput
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={FORM_CTRL_CLASS}
                />
              </CrewReportFilterField>
              <span
                className={cn(
                  "hidden sm:inline pb-2 text-xs",
                  colors.textMuted,
                )}
                aria-hidden
              >
                →
              </span>
              <CrewReportFilterField
                label="To"
                width="sm"
                className="w-[10rem]"
              >
                <DateInput
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={FORM_CTRL_CLASS}
                />
              </CrewReportFilterField>
              <CrewReportFilterField
                label={kind === "VISIT" ? "Visit port" : "Change port"}
                width="lg"
                className="w-[14rem] max-w-full"
              >
                <Select
                  value={changePortId}
                  onChange={(e) => setChangePortId(String(e.target.value))}
                  options={portOptions}
                  searchPlaceholder="Search ports…"
                  triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                />
              </CrewReportFilterField>
            </div>

            <div className="flex flex-wrap items-end gap-2">
              <CrewReportFilterField
                label="Purpose"
                width="lg"
                className="w-[16rem] max-w-full"
              >
                <TextInput
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder={
                    kind === "VISIT" ? "e.g. Audit visit" : "e.g. Master relief"
                  }
                  className={FORM_CTRL_CLASS}
                />
              </CrewReportFilterField>
              <CrewReportFilterField
                label="Notes"
                width="xl"
                className="w-[22rem] max-w-full"
              >
                <TextInput
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional booking notes…"
                  className={FORM_CTRL_CLASS}
                />
              </CrewReportFilterField>
            </div>
          </div>
        </FormPanel>

        <FormPanel
          title="Travelers"
          subtitle={kind === "VISIT" ? "Any role" : "S/Off left · S/On right"}
          icon={<UserIcon className="h-3.5 w-3.5" />}
          action={
            <div className="flex items-center gap-1.5">
              <CountChip>
                {namedTravelerCount}/{passengers.length}
              </CountChip>
              {kind === "VISIT" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  onClick={() =>
                    setPassengers((prev) => [
                      ...prev,
                      emptyPassenger("VISIT", "OTHER"),
                    ])
                  }
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Add
                </Button>
              ) : null}
            </div>
          }
          className={kind === "CREW_CHANGE" ? "overflow-hidden" : undefined}
          bodyClassName={kind === "CREW_CHANGE" ? "!p-0" : undefined}
        >
          {kind === "CREW_CHANGE" ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-stretch">
              {(
                [
                  {
                    side: "SOFF" as const,
                    title: "Sign-off",
                    hint: "Vessel crew",
                    accent: isDark
                      ? "border-amber-500/50 bg-amber-500/[0.06]"
                      : "border-amber-400/70 bg-amber-50/40",
                    badge: isDark
                      ? "bg-amber-500/20 text-amber-200"
                      : "bg-amber-100 text-amber-900",
                    avatar: isDark
                      ? "bg-amber-500/25 text-amber-100"
                      : "bg-amber-100 text-amber-800",
                    options: soffCrewOptions,
                    empty: !vesselId
                      ? "Pick a vessel first"
                      : soffCrewOptions.length
                        ? "Add outgoing traveler"
                        : "No onboard crew",
                  },
                  {
                    side: "SON" as const,
                    title: "Sign-on",
                    hint: "Relief candidates",
                    accent: isDark
                      ? "border-emerald-500/50 bg-emerald-500/[0.06]"
                      : "border-emerald-400/70 bg-emerald-50/40",
                    badge: isDark
                      ? "bg-emerald-500/20 text-emerald-200"
                      : "bg-emerald-100 text-emerald-900",
                    avatar: isDark
                      ? "bg-emerald-500/25 text-emerald-100"
                      : "bg-emerald-100 text-emerald-800",
                    options: sonCrewOptions,
                    empty: !vesselId
                      ? "Pick a vessel first"
                      : selectedSoffCrewId == null
                        ? "Select S/Off first"
                        : sonCrewOptions.length
                          ? "Add relief (optional)"
                          : "No candidates yet",
                  },
                ] as const
              ).map((col, colIdx) => {
                const rows = passengers
                  .map((p, idx) => ({ p, idx }))
                  .filter(({ p }) => p.side === col.side);
                return (
                  <React.Fragment key={col.side}>
                    {colIdx === 1 ? (
                      <div
                        className={cn(
                          "hidden lg:flex items-center justify-center px-0.5 border-x",
                          colors.border,
                          isDark ? "bg-white/[0.02]" : "bg-slate-50/80",
                        )}
                        aria-hidden
                      >
                        <div
                          className={cn(
                            "flex h-7 w-7 items-center justify-center rounded-full border",
                            colors.border,
                            colors.surface,
                            colors.textMuted,
                          )}
                        >
                          <ChevronRightIcon className="h-3.5 w-3.5" />
                        </div>
                      </div>
                    ) : null}
                    <div
                      className={cn(
                        "flex flex-col min-w-0 min-h-0 border-t-2 lg:border-t-0 lg:border-l-2",
                        col.accent,
                        colIdx === 0 && "lg:border-l-0",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                        <div className="min-w-0 flex items-center gap-1.5">
                          <span
                            className={cn(
                              "inline-flex h-5 items-center rounded px-1.5 text-[10px] font-semibold",
                              col.badge,
                            )}
                          >
                            {col.side === "SOFF" ? "S/Off" : "S/On"}
                          </span>
                          <span
                            className={cn("text-xs font-semibold", colors.text)}
                          >
                            {col.title}
                          </span>
                          <span
                            className={cn(
                              "text-[10px] truncate hidden sm:inline",
                              colors.textMuted,
                            )}
                          >
                            {col.hint}
                          </span>
                          <CountChip>{rows.length}</CountChip>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="gap-1 shrink-0 h-7 px-2"
                          onClick={() =>
                            setPassengers((prev) => [
                              ...prev,
                              emptyPassenger(col.side, "CREW"),
                            ])
                          }
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                          Add
                        </Button>
                      </div>

                      <div className="flex-1 px-2 pb-2 space-y-1.5">
                        {rows.length === 0 ? (
                          <div
                            className={cn(
                              "rounded-md border border-dashed px-2 py-3 text-center",
                              colors.border,
                            )}
                          >
                            <p className={cn("text-[11px]", colors.textMuted)}>
                              {col.empty}
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="mt-2 gap-1 h-7"
                              disabled={
                                col.side === "SON" &&
                                (!vesselId || selectedSoffCrewId == null)
                              }
                              onClick={() =>
                                setPassengers((prev) => [
                                  ...prev,
                                  emptyPassenger(col.side, "CREW"),
                                ])
                              }
                            >
                              <PlusIcon className="h-3 w-3" />
                              Add
                            </Button>
                          </div>
                        ) : (
                          rows.map(({ p, idx }) => {
                            const selected = Boolean(p.crewId && p.displayName);
                            return (
                              <div
                                key={`p-${col.side}-${idx}`}
                                className={cn(
                                  "group relative rounded-md border px-2 py-1.5",
                                  colors.border,
                                  colors.surface,
                                  isDark
                                    ? "hover:bg-white/[0.03]"
                                    : "hover:bg-slate-50/80",
                                )}
                              >
                                <div className="flex gap-2 items-center">
                                  <div
                                    className={cn(
                                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                                      col.avatar,
                                    )}
                                    title={
                                      selected
                                        ? [p.displayName, p.nationality]
                                            .filter(Boolean)
                                            .join(" · ")
                                        : undefined
                                    }
                                  >
                                    {selected ? (
                                      initialsFor(p.displayName)
                                    ) : (
                                      <UserIcon className="h-3.5 w-3.5 opacity-70" />
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1 flex flex-wrap items-center gap-1.5 pr-7">
                                    <div className="w-[min(100%,16rem)] min-w-[10rem] flex-1 max-w-[18rem]">
                                      <Select
                                        value={
                                          p.crewId != null
                                            ? String(p.crewId)
                                            : ""
                                        }
                                        onChange={(e) => {
                                          const raw = String(e.target.value);
                                          const id = raw ? Number(raw) : NaN;
                                          const crewId =
                                            Number.isFinite(id) && id > 0
                                              ? id
                                              : null;
                                          selectChangeCrew(
                                            idx,
                                            col.side,
                                            crewId,
                                            col.options,
                                          );
                                        }}
                                        options={[
                                          { value: "", label: "—" },
                                          ...col.options,
                                        ]}
                                        placeholder={
                                          !vesselId
                                            ? "Vessel first…"
                                            : col.side === "SON"
                                              ? selectedSoffCrewId == null
                                                ? "S/Off first…"
                                                : col.options.length
                                                  ? "Select relief…"
                                                  : "No candidates"
                                              : col.options.length
                                                ? "Select outgoing…"
                                                : "No onboard crew"
                                        }
                                        searchPlaceholder="Search…"
                                        showClearButton
                                        disabled={
                                          !vesselId ||
                                          (col.side === "SON" &&
                                            selectedSoffCrewId == null)
                                        }
                                        triggerClassName={
                                          FORM_SELECT_TRIGGER_CLASS
                                        }
                                      />
                                    </div>
                                    <div className="w-[7.5rem]">
                                      <TextInput
                                        value={p.passport || ""}
                                        onChange={(e) =>
                                          setPassengers((prev) =>
                                            prev.map((row, i) =>
                                              i === idx
                                                ? {
                                                    ...row,
                                                    passport:
                                                      e.target.value || null,
                                                  }
                                                : row,
                                            ),
                                          )
                                        }
                                        placeholder="Passport"
                                        className={FORM_CTRL_CLASS}
                                      />
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className={cn(
                                      'absolute top-0.5 right-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-40',
                                      isDark
                                        ? 'text-red-400 hover:bg-red-950/50'
                                        : 'text-red-600 hover:bg-red-50',
                                    )}
                                    disabled={!canRemovePassenger(p)}
                                    onClick={() =>
                                      setPassengers((prev) =>
                                        prev.filter((_, i) => i !== idx),
                                      )
                                    }
                                    title="Remove traveler"
                                    aria-label="Remove traveler"
                                  >
                                    <TrashIcon className="h-4 w-4 shrink-0" />
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1.5">
              {passengers.map((p, idx) => (
                <div
                  key={`p-${idx}`}
                  className={cn(
                    "group relative rounded-md border px-2 py-1.5",
                    colors.border,
                    isDark ? "bg-white/[0.02]" : "bg-slate-50/40",
                  )}
                >
                  <div className="flex flex-wrap items-end gap-2 pr-7">
                    <CrewReportFilterField
                      label="Name"
                      width="xl"
                      className="w-[16rem] max-w-full"
                    >
                      <TextInput
                        value={p.displayName}
                        onChange={(e) =>
                          setPassengers((prev) =>
                            prev.map((row, i) =>
                              i === idx
                                ? { ...row, displayName: e.target.value }
                                : row,
                            ),
                          )
                        }
                        placeholder="Full name"
                        className={cn(FORM_CTRL_CLASS, "font-medium")}
                      />
                    </CrewReportFilterField>
                    <CrewReportFilterField
                      label="Role"
                      width="md"
                      className="w-[11rem]"
                    >
                      <Select
                        triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                        value={p.role}
                        onChange={(e) => {
                          const role = String(
                            e.target.value,
                          ) as CrewTravelTravelerRole;
                          setPassengers((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, role } : row,
                            ),
                          );
                        }}
                        options={ROLE_OPTIONS}
                      />
                    </CrewReportFilterField>
                    <CrewReportFilterField
                      label="Nationality"
                      width="md"
                      className="w-[11rem]"
                    >
                      <Select
                        triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                        value={
                          p.nationalityId != null ? String(p.nationalityId) : ""
                        }
                        onChange={(e) => {
                          const raw = String(e.target.value);
                          const nationalityId = raw ? Number(raw) : NaN;
                          const resolvedId =
                            Number.isFinite(nationalityId) && nationalityId > 0
                              ? nationalityId
                              : null;
                          const nat = lookups?.nationalities?.find(
                            (n) => n.id === resolvedId,
                          );
                          setPassengers((prev) =>
                            prev.map((row, i) =>
                              i === idx
                                ? {
                                    ...row,
                                    nationalityId: resolvedId,
                                    nationality: nat?.name ?? null,
                                  }
                                : row,
                            ),
                          );
                        }}
                        options={[
                          { value: "", label: "—" },
                          ...nationalityOptionsBase,
                        ]}
                        placeholder="Select…"
                        searchPlaceholder="Search…"
                      />
                    </CrewReportFilterField>
                    <CrewReportFilterField
                      label="Passport / ID"
                      width="md"
                      className="w-[11rem]"
                    >
                      <TextInput
                        value={p.passport || ""}
                        onChange={(e) =>
                          setPassengers((prev) =>
                            prev.map((row, i) =>
                              i === idx
                                ? { ...row, passport: e.target.value || null }
                                : row,
                            ),
                          )
                        }
                        placeholder="Document no."
                        className={FORM_CTRL_CLASS}
                      />
                    </CrewReportFilterField>
                    <div className="absolute top-1 right-1">
                      <button
                        type="button"
                        className={cn(
                          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-40',
                          isDark
                            ? 'text-red-400 hover:bg-red-950/50'
                            : 'text-red-600 hover:bg-red-50',
                        )}
                        disabled={!canRemovePassenger(p)}
                        onClick={() =>
                          setPassengers((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                        title="Remove traveler"
                        aria-label="Remove traveler"
                      >
                        <TrashIcon className="h-4 w-4 shrink-0" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FormPanel>

        <FormPanel
          title="Flight Details"
          subtitle={
            kind === "CREW_CHANGE"
              ? "S/Off & S/On legs"
              : "Flights, hotels, transfers"
          }
          icon={<MapPinIcon className="h-3.5 w-3.5" />}
          action={
            <div className="flex items-center gap-1.5">
              <CountChip>
                {filledSegmentCount}/{segments.length || 0}
              </CountChip>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() =>
                  setSegments((prev) => [
                    ...prev,
                    makeEmptySegment(kind === "VISIT" ? "VISIT" : "SOFF"),
                  ])
                }
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add leg
              </Button>
            </div>
          }
        >
          {segments.length === 0 ? (
            <div
              className={cn(
                "rounded-md border border-dashed px-3 py-5 text-center",
                colors.border,
              )}
            >
              <div className={cn("text-xs font-medium", colors.text)}>
                No legs yet
              </div>
              <p className={cn("text-[11px] mt-0.5", colors.textMuted)}>
                Add a flight, hotel, or transfer
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2 gap-1 h-7"
                onClick={() =>
                  setSegments([
                    makeEmptySegment(kind === "VISIT" ? "VISIT" : "SOFF"),
                  ])
                }
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add leg
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {segments.map((s, idx) => {
                const sideTone =
                  s.side === "SON"
                    ? isDark
                      ? "bg-emerald-500/20 text-emerald-200"
                      : "bg-emerald-100 text-emerald-900"
                    : s.side === "SOFF"
                      ? isDark
                        ? "bg-amber-500/20 text-amber-200"
                        : "bg-amber-100 text-amber-900"
                      : isDark
                        ? "bg-sky-500/20 text-sky-200"
                        : "bg-sky-100 text-sky-900";

                return (
                  <div
                    key={`s-${idx}`}
                    className={cn(
                      "group relative rounded-md border px-2.5 py-2",
                      colors.border,
                      colors.surface,
                      isDark ? "hover:bg-white/[0.02]" : "hover:bg-slate-50/50",
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-1.5 mb-2 pr-7">
                      <span
                        className={cn(
                          "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded px-1 text-[10px] font-semibold tabular-nums",
                          isDark
                            ? "bg-white/10 text-white/80"
                            : "bg-slate-100 text-slate-700",
                        )}
                      >
                        {idx + 1}
                      </span>
                      <div className="w-[7.5rem]">
                        <Select
                          value={s.kind}
                          onChange={(e) => {
                            const next = String(
                              e.target.value,
                            ).toUpperCase() as CrewTravelSegmentKind;
                            const opt = segmentKindOptions.find(
                              (o) => o.value === next,
                            );
                            setSegments((prev) =>
                              prev.map((row, i) =>
                                i === idx
                                  ? {
                                      ...row,
                                      kind: next,
                                      segmentTypeId: opt?.id ?? null,
                                      kindName: opt?.label ?? null,
                                    }
                                  : row,
                              ),
                            );
                          }}
                          options={
                            segmentKindOptions.length
                              ? segmentKindOptions
                              : [{ value: s.kind || "FLIGHT", label: s.kindName || s.kind || "—" }]
                          }
                          triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                        />
                      </div>
                      {kind === "CREW_CHANGE" ? (
                        <div className="w-[8rem]">
                          <Select
                            value={s.side || ""}
                            onChange={(e) => {
                              const side = (String(e.target.value) ||
                                null) as CrewTravelSide | null;
                              setSegments((prev) =>
                                prev.map((row, i) =>
                                  i === idx ? { ...row, side } : row,
                                ),
                              );
                            }}
                            options={[
                              { value: "", label: "—" },
                              ...SIDE_OPTIONS_CHANGE,
                            ]}
                            triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                          />
                        </div>
                      ) : null}
                      {s.side === "SOFF" || s.side === "SON" ? (
                        <span
                          className={cn(
                            "inline-flex h-5 items-center rounded px-1.5 text-[10px] font-semibold",
                            sideTone,
                          )}
                        >
                          {s.side === "SON" ? "S/On" : "S/Off"}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className={cn(
                          'absolute top-1 right-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
                          isDark
                            ? 'text-red-400 hover:bg-red-950/50'
                            : 'text-red-600 hover:bg-red-50',
                        )}
                        onClick={() =>
                          setSegments((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                        title="Remove leg"
                        aria-label="Remove leg"
                      >
                        <TrashIcon className="h-4 w-4 shrink-0" />
                      </button>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex flex-wrap items-end gap-2">
                        {s.kind === "FLIGHT" ? (
                          <>
                            <CrewReportFilterField
                              label="Start"
                              width="fill"
                              className="w-[13rem] max-w-full"
                            >
                              <DateTimeInput
                                value={s.startAt || ""}
                                onChange={(e) =>
                                  setSegments((prev) =>
                                    prev.map((row, i) =>
                                      i === idx
                                        ? {
                                            ...row,
                                            startAt: e.target.value || null,
                                            travelDate: e.target.value
                                              ? e.target.value.slice(0, 10)
                                              : row.travelDate,
                                          }
                                        : row,
                                    ),
                                  )
                                }
                                className={FORM_CTRL_CLASS}
                              />
                            </CrewReportFilterField>
                            <CrewReportFilterField
                              label="End"
                              width="fill"
                              className="w-[13rem] max-w-full"
                            >
                              <DateTimeInput
                                value={s.endAt || ""}
                                onChange={(e) =>
                                  setSegments((prev) =>
                                    prev.map((row, i) =>
                                      i === idx
                                        ? {
                                            ...row,
                                            endAt: e.target.value || null,
                                          }
                                        : row,
                                    ),
                                  )
                                }
                                className={FORM_CTRL_CLASS}
                              />
                            </CrewReportFilterField>
                          </>
                        ) : (
                          <CrewReportFilterField
                            label="Date"
                            width="sm"
                            className="w-[10rem]"
                          >
                            <DateInput
                              value={s.travelDate || ""}
                              onChange={(e) =>
                                setSegments((prev) =>
                                  prev.map((row, i) =>
                                    i === idx
                                      ? {
                                          ...row,
                                          travelDate: e.target.value || null,
                                        }
                                      : row,
                                  ),
                                )
                              }
                              className={FORM_CTRL_CLASS}
                            />
                          </CrewReportFilterField>
                        )}

                        <CrewReportFilterField
                          label={s.kind === "FLIGHT" ? "Start flight" : "From"}
                          width="md"
                          className="w-[11rem]"
                        >
                          <TextInput
                            value={s.fromPlace || ""}
                            onChange={(e) =>
                              setSegments((prev) =>
                                prev.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        fromPlace: e.target.value || null,
                                      }
                                    : row,
                                ),
                              )
                            }
                            placeholder={
                              s.kind === "FLIGHT" ? "ATH / A3 620" : "Place"
                            }
                            className={FORM_CTRL_CLASS}
                          />
                        </CrewReportFilterField>
                        <span
                          className={cn(
                            "hidden sm:inline pb-2 text-xs",
                            colors.textMuted,
                          )}
                          aria-hidden
                        >
                          →
                        </span>
                        <CrewReportFilterField
                          label={s.kind === "FLIGHT" ? "End flight" : "To"}
                          width="md"
                          className="w-[11rem]"
                        >
                          <TextInput
                            value={s.toPlace || ""}
                            onChange={(e) =>
                              setSegments((prev) =>
                                prev.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        toPlace: e.target.value || null,
                                      }
                                    : row,
                                ),
                              )
                            }
                            placeholder={
                              s.kind === "FLIGHT" ? "LHR / A3 621" : "Place"
                            }
                            className={FORM_CTRL_CLASS}
                          />
                        </CrewReportFilterField>
                      </div>

                      <div className="flex flex-wrap items-end gap-2">
                        <CrewReportFilterField
                          label="PNR / booking"
                          width="md"
                          className="w-[11rem]"
                        >
                          <TextInput
                            value={s.reference || ""}
                            onChange={(e) =>
                              setSegments((prev) =>
                                prev.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        reference: e.target.value || null,
                                      }
                                    : row,
                                ),
                              )
                            }
                            placeholder="Booking ref"
                            className={FORM_CTRL_CLASS}
                          />
                        </CrewReportFilterField>
                        <CrewReportFilterField
                          label="Cost"
                          width="sm"
                          className="w-[8rem]"
                        >
                          <NumberInput
                            value={s.cost ?? ""}
                            onChange={(e) => {
                              const n = e.target.value;
                              setSegments((prev) =>
                                prev.map((row, i) =>
                                  i === idx ? { ...row, cost: n } : row,
                                ),
                              );
                            }}
                            placeholder="0.00"
                            allowDecimals
                            className={FORM_CTRL_CLASS}
                          />
                        </CrewReportFilterField>
                        <CrewReportFilterField
                          label="Currency"
                          width="sm"
                          className="w-[7.5rem]"
                        >
                          <Select
                            value={s.currency || "EUR"}
                            onChange={(e) =>
                              setSegments((prev) =>
                                prev.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        currency: e.target.value || null,
                                      }
                                    : row,
                                ),
                              )
                            }
                            options={currencyOptions}
                            searchPlaceholder="Search…"
                            triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                          />
                        </CrewReportFilterField>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </FormPanel>
      </div>

      <div
        className={cn(
          "shrink-0 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 shadow-sm",
          colors.border,
          colors.surface,
        )}
      >
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <CountChip>
            {namedTravelerCount} traveler{namedTravelerCount === 1 ? "" : "s"}
          </CountChip>
          <CountChip>
            {filledSegmentCount} leg{filledSegmentCount === 1 ? "" : "s"}
          </CountChip>
          {vesselId ? (
            <span
              className={cn("text-xs truncate max-w-[16rem]", colors.textMuted)}
            >
              {vesselOptions.find((v) => v.value === vesselId)?.label ??
                `Vessel #${vesselId}`}
            </span>
          ) : (
            <span className={cn("text-xs", colors.textMuted)}>
              No vessel selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() =>
              navigate(
                !isNew && Number.isFinite(travelId)
                  ? `/crew/crew-changes-cost/${travelId}`
                  : "/crew/crew-changes-cost",
              )
            }
          >
            Cancel
          </Button>
          <Button type="button" disabled={saving} onClick={() => void save()}>
            {saving
              ? isNew
                ? "Creating…"
                : "Saving…"
              : isNew
                ? "Create"
                : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TravelTicketFormPage;
